import React from 'react';
import { cn } from '../../lib/utils';

// Reusable Section component for Guide articles
export function Section({ 
  emoji, 
  title, 
  children,
  accentColor = 'teal'
}: { 
  emoji: string; 
  title: string; 
  children: React.ReactNode;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green' | 'red';
}) {
  const borderColors = {
    teal: 'border-teal-500',
    purple: 'border-purple-500',
    amber: 'border-amber-500',
    rose: 'border-rose-500',
    blue: 'border-blue-500',
    emerald: 'border-emerald-500',
    orange: 'border-orange-500',
    green: 'border-green-500',
    red: 'border-error-red',
  };

  return (
    <section className="mb-10">
      <div className="mb-4">
        <h2 className="text-lg lg:text-xl font-black uppercase tracking-wider text-text-primary">
          {title}
        </h2>
      </div>
      <div className={cn("border-l-4 pl-4 space-y-4 text-text-secondary", borderColors[accentColor])}>
        {children}
      </div>
    </section>
  );
}

// Tip component for labeled tips
export function Tip({ 
  label, 
  text,
  accentColor = 'teal'
}: { 
  label: string; 
  text: string;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green' | 'red';
}) {
  const labelColors = {
    teal: 'text-teal-700',
    purple: 'text-purple-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    green: 'text-positive-green',
    red: 'text-error-red',
  };

  return (
    <div>
      <span className={cn("font-bold", labelColors[accentColor])}>{label}: </span>
      <span className="text-sm leading-relaxed">{text}</span>
    </div>
  );
}

// Callout component for highlighted notes
export function Callout({ 
  children,
  accentColor = 'teal'
}: { 
  children: React.ReactNode;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green' | 'red';
}) {
  const colorClasses = {
    teal: 'bg-teal-50 text-teal-800 border-teal-100',
    purple: 'bg-purple-50 text-purple-800 border-purple-100',
    amber: 'bg-amber-50 text-amber-800 border-amber-100',
    rose: 'bg-rose-50 text-rose-800 border-rose-100',
    blue: 'bg-blue-50 text-blue-800 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    orange: 'bg-orange-50 text-orange-800 border-orange-100',
    green: 'bg-green-50 text-green-800 border-green-100',
    red: 'bg-error-red\/5 text-error-red border-error-red\/10',
  };

  return (
    <div className={cn("rounded-xl px-5 py-4 text-sm font-medium italic leading-relaxed border", colorClasses[accentColor])}>
      {children}
    </div>
  );
}

// Article image component
export function ArticleImage({ 
  src, 
  alt, 
  caption 
}: { 
  src: string; 
  alt: string; 
  caption?: string;
}) {
  return (
    <figure className="my-8 -mx-1">
      <img
        src={src}
        alt={alt}
        className="w-full h-52 sm:h-64 lg:h-80 object-cover rounded-2xl"
        loading="lazy"
      />
      {caption && (
        <figcaption className="text-xs text-gray-400 text-center mt-2 italic">{caption}</figcaption>
      )}
    </figure>
  );
}

// Disclaimer component for legal/medical notices
export function Disclaimer({ 
  title, 
  children 
}: { 
  title: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl p-5 text-xs leading-relaxed mt-4 mb-8 bg-gray-50 text-text-secondary border border-gray-200">
      <p className="font-bold text-sm mb-1 text-text-secondary">{title}</p>
      <p>{children}</p>
    </div>
  );
}

// ToolsTable component for medication management tools
export function ToolsTable({ 
  rows,
  toolHeader = 'Tool',
  whyHeader = 'Why It Works',
  accentColor = 'purple'
}: { 
  rows: { tool: string; why: string }[];
  toolHeader?: string;
  whyHeader?: string;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green';
}) {
  const labelColors = {
    teal: 'text-teal-700',
    purple: 'text-purple-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    green: 'text-positive-green',
  };

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm border-separate border-spacing-y-2">
        <thead>
          <tr className="text-text-secondary">
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1 pr-4 pl-1 w-2/5">{toolHeader}</th>
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1">{whyHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.tool} className="bg-gray-50">
              <td className={cn("font-bold rounded-l-xl py-3 pl-3 pr-4 align-top", labelColors[accentColor])}>
                {row.tool}
              </td>
              <td className="rounded-r-xl py-3 pr-3 align-top leading-relaxed italic text-text-secondary">
                {row.why}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TimelineTable component for food timeline phases
export function TimelineTable({ 
  rows,
  accentColor = 'orange'
}: { 
  rows: { phase: string; duration: string; focus: string; restrictions: string }[];
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green';
}) {
  const labelColors = {
    teal: 'text-teal-700',
    purple: 'text-purple-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    green: 'text-positive-green',
  };

  return (
    <div className="overflow-x-auto -mx-1 space-y-2">
      {rows.map((row) => (
        <div key={row.phase} className="rounded-xl p-4 bg-gray-50">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
            <span className={cn("font-bold text-sm", labelColors[accentColor])}>{row.phase}</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{row.duration}</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-wide mb-1 text-text-secondary">{row.focus}</p>
          <p className="text-sm leading-relaxed italic text-text-secondary">{row.restrictions}</p>
        </div>
      ))}
    </div>
  );
}

// ActivityTable component for exercise activities
export function ActivityTable({ 
  rows,
  activityHeader = 'Activity',
  whyHeader = "Why It's Great",
  tipHeader = 'Pro Tip',
  accentColor = 'green'
}: { 
  rows: { activity: string; why: string; tip: string }[];
  activityHeader?: string;
  whyHeader?: string;
  tipHeader?: string;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green';
}) {
  const labelColors = {
    teal: 'text-teal-700',
    purple: 'text-purple-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    green: 'text-positive-green',
  };

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm border-separate border-spacing-y-2">
        <thead>
          <tr className="text-text-secondary">
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1 pr-4 pl-1">{activityHeader}</th>
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1 pr-4">{whyHeader}</th>
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1">{tipHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.activity} className="rounded-xl bg-gray-50">
              <td className={cn("font-bold rounded-l-xl py-3 pl-3 pr-4 align-top whitespace-nowrap", labelColors[accentColor])}>
                {row.activity}
              </td>
              <td className="py-3 pr-4 align-top leading-relaxed text-text-secondary">
                {row.why}
              </td>
              <td className="rounded-r-xl py-3 pr-3 align-top leading-relaxed italic text-text-secondary">
                {row.tip}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// EmotionalTable component for obstacles/emotional challenges
export function EmotionalTable({ 
  rows,
  feelingHeader = 'The Feeling',
  whyHeader = 'Why It Happens',
  strategyHeader = 'The Strategy',
  accentColor = 'red'
}: { 
  rows: { feeling: string; why: string; strategy: string }[];
  feelingHeader?: string;
  whyHeader?: string;
  strategyHeader?: string;
  accentColor?: 'teal' | 'purple' | 'amber' | 'rose' | 'blue' | 'emerald' | 'orange' | 'green' | 'red';
}) {
  const labelColors = {
    teal: 'text-teal-700',
    purple: 'text-purple-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    green: 'text-positive-green',
    red: 'text-error-red',
  };

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm border-separate border-spacing-y-2">
        <thead>
          <tr className="text-text-secondary">
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1 pr-4 pl-1">{feelingHeader}</th>
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1 pr-4">{whyHeader}</th>
            <th className="text-left font-bold uppercase text-xs tracking-wider pb-1">{strategyHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.feeling} className="bg-gray-50">
              <td className={cn("font-bold rounded-l-xl py-3 pl-3 pr-4 align-top whitespace-nowrap", labelColors[accentColor])}>
                {row.feeling}
              </td>
              <td className="py-3 pr-4 align-top leading-relaxed text-text-secondary">
                {row.why}
              </td>
              <td className="rounded-r-xl py-3 pr-3 align-top leading-relaxed italic text-text-secondary">
                {row.strategy}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// MDX components mapping for automatic styling
export const mdxComponents = {
  Section,
  Tip,
  Callout,
  ArticleImage,
  Disclaimer,
  ToolsTable,
  TimelineTable,
  ActivityTable,
  EmotionalTable,
  // Standard markdown element overrides
  h1: ({ children }: { children: React.ReactNode }) => (
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4 text-text-primary">{children}</h1>
  ),
  h2: ({ children }: { children: React.ReactNode }) => (
    <h2 className="text-lg lg:text-xl font-black uppercase tracking-wider text-text-primary mb-4">{children}</h2>
  ),
  p: ({ children }: { children: React.ReactNode }) => (
    <p className="text-sm leading-relaxed text-text-secondary mb-4">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-bold text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children: React.ReactNode }) => (
    <em className="italic">{children}</em>
  ),
  hr: () => <div className="border-t mb-10 border-gray-100" />,
};
